import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Edit, Eye, Trash2, Plus, Search, X } from 'lucide-react';
import api from '../../utils/api';

interface Company {
  id: number;
  raison_social: string;
  email: string;
  phone: string;
  address: string;
  ice: string;
  rib: string;
  client_type: string;
  created_at: string;
  updated_at: string;
  avatar: string | null;
}

interface LazyParams {
  first: number;
  rows: number;
  page: number;
  sortField: string | null;
  sortOrder: 'asc' | 'desc' | null;
  filters: Record<string, any>;
}

export const CompanyList: React.FC<{
  companies: Company[];
  loading: boolean;
  totalRecords: number;
  lazyParams: LazyParams;
  setLazyParams: React.Dispatch<React.SetStateAction<LazyParams>>;
  onCreateCompany: () => void;
  onEditCompany: (company: Company) => void;
  onViewCompany: (company: Company) => void;
  onDeleteCompany: (company: Company) => void;
}> = ({
  companies,
  loading,
  totalRecords,
  lazyParams,
  setLazyParams,
  onCreateCompany,
  onEditCompany,
  onViewCompany,
  onDeleteCompany,
}) => {
  const { t } = useTranslation();
  const [globalFilter, setGlobalFilter] = useState('');
  const [toast, setToast] = useState({ visible: false, severity: '', message: '' });

  const onPage = (page: number) => {
    setLazyParams((prevState) => ({ ...prevState, page, first: (page - 1) * prevState.rows }));
  };

  const onSort = (field: string) => {
    setLazyParams((prevState) => ({
      ...prevState,
      sortField: field,
      sortOrder: prevState.sortField === field && prevState.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    setLazyParams((prevState) => ({
      ...prevState,
      first: 0,
      page: 1,
      filters: { ...prevState.filters, global: value },
    }));
  };

  const showToast = (severity: string, message: string) => {
    setToast({ visible: true, severity, message });
    setTimeout(() => setToast({ ...toast, visible: false }), 3000);
  };

  const handleDeleteCompany = async (company: Company) => {
    try {
      await api.delete(`/companies/${company.id}`);
      showToast('success', t('company_deleted_successfully'));
      setLazyParams(prevState => ({...prevState, page: 1, first: 0}));
    } catch (error) {
      console.error('Error deleting company:', error);
      showToast('error', t('error_deleting_company'));
    }
  }

  const confirmDeleteCompany = (company: Company) => {
    if (window.confirm(t('confirm_delete_company'))) {
      handleDeleteCompany(company);
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">{t('companies')}</h2>
          <div className="flex items-center">
            <div className="relative mr-2">
              <input
                type="text"
                className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('search_companies')}
                value={globalFilter}
                onChange={onGlobalFilterChange}
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            </div>
            <button
              onClick={onCreateCompany}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
            >
              <Plus className="inline-block mr-2" size={18} />
              {t('new_company')}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('logo')}
                </th>
                {['raison_social', 'email', 'phone', 'client_type'].map((field) => (
                  <th
                    key={field}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => onSort(field)}
                  >
                    {t(field)}
                    {lazyParams.sortField === field && (
                      <span className="ml-2">
                        {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </th>
                ))}
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    {t('loading')}
                  </td>
                </tr>
              ) : companies.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    {t('no_companies_found')}
                  </td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {company.avatar ? (
                        <img src={`${process.env.REACT_APP_PIC_STORAGE}/${company.avatar}`} alt={`${company.raison_social} logo`} className="h-10 w-10 rounded-full object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 font-bold">{company.raison_social.charAt(0)}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{company.raison_social}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{company.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{company.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{company.client_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => onEditCompany(company)}
                        className="text-indigo-600 hover:text-indigo-900 mr-2 focus:outline-none focus:underline"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => onViewCompany(company)}
                        className="text-blue-600 hover:text-blue-900 mr-2 focus:outline-none focus:underline"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => confirmDeleteCompany(company)}
                        className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div>
            <p className="text-sm text-gray-700">
              {t('showing')} <span className="font-medium">{lazyParams.first + 1}</span> {t('to')} <span className="font-medium">{Math.min(lazyParams.first + lazyParams.rows, totalRecords)}</span> {t('of')}{' '}
              <span className="font-medium">{totalRecords}</span> {t('results')}
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => onPage(1)}
                disabled={lazyParams.page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('first')}</span>
                <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => onPage(lazyParams.page - 1)}
                disabled={lazyParams.page === 1}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('previous')}</span>
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                {t('page')} {lazyParams.page} {t('of')} {Math.ceil(totalRecords / lazyParams.rows)}
              </span>
              <button
                onClick={() => onPage(lazyParams.page + 1)}
                disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows)}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('next')}</span>
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => onPage(Math.ceil(totalRecords / lazyParams.rows))}
                disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows)}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('last')}</span>
                <ChevronsRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

